import {
  BaseApiService,
  type JsonApiResource,
  type JsonApiResponse,
  type ServiceOptions,
} from './base.service';

// The two material kinds that can be re-filed into one another in the Brief.
// A "view" (angle) can become a "doc" (brief reference) and vice versa.
// Sketches (controls) are intentionally NOT convertible.
export type MaterialType = 'view' | 'doc';

export interface ConvertMaterialTypeParams {
  source_type: MaterialType;
  source_id: string;
  target_type: MaterialType;
}

export class MaterialsService extends BaseApiService {
  /**
   * Re-file a Brief material from one type to another (view <-> doc) in place.
   *
   * A View card converts with { source_type: 'view', source_id: angle.id,
   * target_type: 'doc' }; a Doc card with { source_type: 'doc', source_id:
   * reference.id, target_type: 'view' }. Server returns 201 with the newly
   * created entity (angle or reference). On a 4xx the API returns a JSON:API
   * error whose `detail` is shown verbatim — there is NO client-side copy for
   * the rejection (e.g. "view has renders", "only image documents").
   */
  async convertType(
    projectId: string,
    params: ConvertMaterialTypeParams,
    options?: ServiceOptions
  ): Promise<JsonApiResource> {
    const requestData = this.formatJsonApiData('material_conversion', {
      source_type: params.source_type,
      source_id: params.source_id,
      target_type: params.target_type,
    });

    const response = await this.post<JsonApiResponse<JsonApiResource>>(
      `/v1/projects/${projectId}/materials/convert`,
      requestData,
      undefined,
      {
        loading: { trackEndpoint: `materials-convert-${projectId}` },
        ...options,
      }
    );
    return response.data;
  }
}

export const materialsService = new MaterialsService();
